import Script from "next/script";
import { useSearchParams } from "next/navigation";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useState } from "react";
import { Anchor, Button } from "@newsela/angelou";

dayjs.extend(timezone);

// Interface for the Salesforce based Embedded Chat Service.
interface EmbeddedChatService {
  init(
    id: string,
    label: string,
    messagingUrl: string,
    options: {
      scrt2URL: string;
    },
  ): void;
  prechatAPI: {
    setHiddenPrechatFields(options: { Website: string }): void;
  };
  settings: {
    hideChatButtonOnLoad: boolean;
    language: string;
  };
  utilAPI: {
    launchChat: Function;
  };
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedChatService;
  }
}

const SupportLink = () => (
  <>
    &nbsp;
    <Anchor openInNewTab href="https://support.newsela.com/contact/">
      Contact Support
    </Anchor>
  </>
);

const salesforceChatStart = 8; // 8 am.
const salesforceChatEnd = 20; // 8 pm.

// Salesforce chat support. This allows users to talk to support when they have issues.
// The chat prompt is all loaded from a Salesforce supplied min.js file which needs to be loaded.
// Then, the security information is loaded by the initEmbeddedMessaging call.
export const SupportChat = () => {
  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const params = useSearchParams();

  const salesforceKey =
    process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_KEY"] ?? "";
  const salesforceSiteEndpoint =
    process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SITE_ENDPOINT"] ??
    "";
  const salesforceScrt2Url =
    process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SCRT2URL"] ?? "";
  const hasRequiredConfigForSalesforceSupportChat = Boolean(
    salesforceKey && salesforceSiteEndpoint && salesforceScrt2Url,
  );
  if (!hasRequiredConfigForSalesforceSupportChat) {
    console.error("Could not load Salesforce support chat information.");
  }

  const initEmbeddedMessaging = () => {
    // eslint-disable-next-line no-console
    console.info("Performing init for Salesforce");
    try {
      if (window.embeddedservice_bootstrap) {
        window.embeddedservice_bootstrap.settings.language = "en_US";
        window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;

        window.embeddedservice_bootstrap.init(
          salesforceKey,
          "Pre_Authentication_Messaging_Session",
          salesforceSiteEndpoint,
          {
            scrt2URL: salesforceScrt2Url,
          },
        );

        setIsChatLoaded(true);
      } else {
        console.error("Did not see Salesforce embeddedservice_bootstrap");
      }
    } catch (err) {
      console.error("Error loading Salesforce Embedded Messaging: ", err);
    }
  };

  const launchSalesforceSupportChat = () => {
    if (window.embeddedservice_bootstrap) {
      if (window.embeddedservice_bootstrap.utilAPI) {
        window.embeddedservice_bootstrap.utilAPI
          .launchChat()
          .catch((error: any) => {
            console.error("Failed to launch Salesforce chat client: ", error);
          });
      } else {
        console.error(
          "Salesforce embeddedservice_bootstrap.utilAPI is not available",
        );
      }
    } else {
      console.error("Salesforce embeddedservice_bootstrap is not defined");
    }
  };

  const handleWithinOfficeHours = () => {
    // eslint-disable-next-line no-console
    console.info("Received office hours active.");
    if (params.get("officeHours") === "false") {
      setIsChatReady(false);
      return;
    }
    setIsChatReady(true);
  };

  const handleOutsideOfficeHours = () => {
    // eslint-disable-next-line no-console
    console.info("Received office hours inactive.");
    setIsChatReady(false);
  };

  const handleMessagingReady = () => {
    // eslint-disable-next-line no-console
    console.info("Received the onEmbeddedMessagingReady event.");

    window.embeddedservice_bootstrap!.prechatAPI.setHiddenPrechatFields({
      Website: "Pre-Authentication Newsela",
    });

    if (params.get("officeHours") === "false") {
      setIsChatReady(false);
      return;
    }

    // The support hours are in EST (aka New York).
    const now = dayjs().tz("America/New_York");
    const dayOfWeek = now.day();
    if (dayOfWeek < 1 || dayOfWeek > 5) {
      // eslint-disable-next-line no-console
      console.info("Salesforce chat is not available today.");
      return;
    }

    const startChatHour = now.hour(salesforceChatStart).hour();
    const endChatHour = now.hour(salesforceChatEnd).hour();
    const currentHour = now.hour();

    // M - F 8 - 8
    if (currentHour >= startChatHour && currentHour <= endChatHour) {
      setIsChatReady(true);
    } else {
      // eslint-disable-next-line no-console
      console.info(
        "Salesforce chat is outside of office hours on initialization.",
      );
    }
  };

  useEffect(() => {
    setIsMounted(true);

    if (params?.get("officeHours") === "false") {
      setIsChatReady(false);
    }

    if (!isChatLoaded) {
      return;
    }
    window.addEventListener(
      "onEmbeddedMessagingWithinBusinessHours",
      handleWithinOfficeHours,
    );
    window.addEventListener(
      "onEmbeddedMessagingBusinessHoursStarted",
      handleWithinOfficeHours,
    );
    window.addEventListener(
      "onEmbeddedMessagingBusinessHoursEnded",
      handleOutsideOfficeHours,
    );
    window.addEventListener("onEmbeddedMessagingReady", handleMessagingReady);

    // Clean listeners when unmounted.
    return () => {
      window.removeEventListener(
        "onEmbeddedMessagingWithinBusinessHours",
        handleWithinOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingBusinessHoursStarted",
        handleWithinOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingBusinessHoursEnded",
        handleOutsideOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingReady",
        handleMessagingReady,
      );
    };
  }, [isChatLoaded, params]);

  if (!isMounted) {
    return <SupportLink />;
  }

  return (
    <>
      {hasRequiredConfigForSalesforceSupportChat && (
        <Script
          id="salesforce-chat-support"
          src={`${salesforceSiteEndpoint}/assets/js/bootstrap.min.js`}
          strategy="afterInteractive"
          onLoad={initEmbeddedMessaging}
          onError={(e) => {
            console.error("Failed to load Salesforce script:", e);
          }}
        />
      )}
      <p>
        Need more help?
        {isChatReady ? (
          <Button
            flavor={Button.flavors.tertiary}
            size={Button.sizes.sm}
            onClick={launchSalesforceSupportChat}
          >
            Chat with our support
          </Button>
        ) : (
          <SupportLink />
        )}
        <br />
        (M - F {salesforceChatStart}am to {salesforceChatEnd - 12}pm ET)
      </p>
    </>
  );
};
